/**
 * A root shell's stage size, for viewport fitting and overlay clamping:
 * measured before the first paint and on every resize, and 800 x 600 px
 * while the stage has no size of its own.
 */

import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

interface StageSize {
  w: number;
  h: number;
}

const UNSIZED_STAGE: StageSize = { w: 800, h: 600 };

/** The stage's ref, for the stage element, and its size. */
export function useStageSize(): { stageRef: RefObject<HTMLDivElement | null>; stageSize: StageSize } {
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState(UNSIZED_STAGE);
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => setStageSize({ w: stage.clientWidth || UNSIZED_STAGE.w, h: stage.clientHeight || UNSIZED_STAGE.h });
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(stage);
    return () => resizeObserver.disconnect();
  }, []);
  return { stageRef, stageSize };
}
